import {Project, UnitSystem} from "../../../../api/Projects";
import {Company} from "../../../../api/Companies";
import {Opening} from "../../../../api/Openings";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {useUser} from "../../../../misc/Permission";
import {useReadyCheck} from "../../../../misc/pdf/UseReadyCheck";
import React, {forwardRef} from "react";
import {PDF} from "../../../../misc/pdf/parts/PDF";
import {ClientInfoContextProvider, PDFProjectContext} from "../openings/OpeningsPDF";
import {ImperialDistance} from "../../../../misc/ImperialDistance";
import {MetricDistance} from "../../../../misc/MetricDistance";
import {
    kdFrameConstruction,
    weldFrameConstruction,
} from "../../quote/openings/Const";
import {blankDrawingInfo, ExportColumn} from "../openings/MakeSubmittal";
import {ImageTilePage} from "../openings/ImageTilePage";
import {Contact} from "../../../../api/Contacts";
import {PdfTableServerRendered} from "../openings/pdf/PdfTableServerRendered";
import {screenSlotLabel} from "../openings/screenSlot";

export const OpeningSizePDF = forwardRef(function OpeningSizePDF(props: {
    project: Project;
    client?: Company;
    clientContact?: Contact;
    data: (Opening|QuoteOpening)[]
    sys: UnitSystem;
    columns: ExportColumn[];
    wallTypeImages: string[];
    anchorImages: string[];
    onReady(): void;
}, ref: any) {

    const usr = useUser();
    const ready = useReadyCheck(props.onReady);
    ready.require(!!usr);

    const parser = props.sys === "imperial" ?
        (input: string) => ImperialDistance.parse(input) :
        (input: string) => MetricDistance.parse(input);

    // multi-opening screens: the secondary openings on each primary's frame, as "slot: opening".
    const slotsOfPrimary = new Map<number, string[]>();
    for(const i of props.data) {
        if(!i.drwScreenPrimaryOpening) continue;
        const list = slotsOfPrimary.get(i.drwScreenPrimaryOpening) ?? [];
        list.push(`${i.drwScreenSlot || ""}: ${i.name}`);
        slotsOfPrimary.set(i.drwScreenPrimaryOpening, list);
    }

    const tableData = {
        header: [
            ...props.columns.map(c => c.display),
            "Rough Opening Width",
            "Rough Opening Height",
        ],
        content: props.data.map((i, index) => {

            let width = "";
            let height = "";

            // a secondary of a multi-opening screen has no frame of its own; its primary's row has the size.
            if(i.drwScreenPrimaryOpening) {
                return [
                    ...props.columns.map(c => c.render ? c.render(i, blankDrawingInfo) : (i as any)[c.key]),
                    "Frame of " + screenSlotLabel(i),
                    "",
                ];
            }

            // a drw screen (user or default) reports its outside size through its rendering; used as-is
            const drwOutside = i.drwScreenElevation || i.drwScreenRendering ? i.drwScreenRenderingMeta : null;

            if(drwOutside) {
                width = drwOutside.outsideWidth;
                height = drwOutside.outsideHeight;
            } else if(i.screenElevation && i.screenElevationOutsideWidth && i.screenElevationOutsideHeight) {
                if((i.openingType === "Single" || i.openingType === "Pair")) {
                    if (i.frameConstruction === weldFrameConstruction) {
                        width = parser(i.screenElevationOutsideWidth).add(ImperialDistance.parse("1/4\"")).toString();
                        height = parser(i.screenElevationOutsideHeight).add(ImperialDistance.parse("1/8\"")).toString();
                    } else if (i.frameConstruction === kdFrameConstruction) {
                        width = parser(i.screenElevationOutsideWidth).add(ImperialDistance.parse("-2\"")).toString();
                        height = parser(i.screenElevationOutsideHeight).add(ImperialDistance.parse("-1\"")).toString();
                    }
                } else if(i.openingType === "Window") {
                    if (i.frameConstruction === weldFrameConstruction) {
                        width = parser(i.screenElevationOutsideWidth).add(ImperialDistance.parse("1/4\"")).toString();
                        height = parser(i.screenElevationOutsideHeight).add(ImperialDistance.parse("1/4\"")).toString();
                    } else if (i.frameConstruction === kdFrameConstruction) {
                        width = parser(i.screenElevationOutsideWidth).add(ImperialDistance.parse("-1 1/4\"")).toString();
                        height = parser(i.screenElevationOutsideHeight).add(ImperialDistance.parse("-2\"")).toString();
                    }
                }
            } else {
                const widthRaw = i.inactiveDoorWidth !== "" ?  parser(i.nominalWidth).add(parser(i.inactiveDoorWidth)) : parser(i.nominalWidth);

                let dWidth = widthRaw.add(ImperialDistance.parse("4\""));
                let dHeight = parser(i.nominalHeight).add(ImperialDistance.parse("2\""));

                if (i.frameConstruction === kdFrameConstruction) {
                    dWidth = dWidth.add(ImperialDistance.parse("-2\""));
                    dHeight = dHeight.add(ImperialDistance.parse("-1\""));
                } else if(i.frameConstruction === weldFrameConstruction) {
                    dWidth = dWidth.add(ImperialDistance.parse("1/4\""));
                    dHeight = dHeight.add(ImperialDistance.parse("1/8\""));
                } else {
                    dWidth = parser("0")
                    dHeight = parser("0")
                    console.error("missing frame profile")
                }

                width = dWidth.toString();
                height = dHeight.toString();
            }

            return [
                ...props.columns.map((c, index) => c.render ? c.render(i, blankDrawingInfo) : (i as any)[c.key]),
                slotsOfPrimary.has(i.id) ? `${width}\n(with ${slotsOfPrimary.get(i.id)!.join(", ")})` : width,
                height,
            ]
        })
    }

    return (
        <div ref={ref}>
            <PDF>
                <PDFProjectContext.Provider value={props.project}>
                    <ClientInfoContextProvider client={props.client} clientContact={props.clientContact}>
                        <PdfTableServerRendered value={tableData} />
                        {props.wallTypeImages && <ImageTilePage title={"Wall Types"} images={props.wallTypeImages} />}
                        {props.anchorImages && <ImageTilePage title={"Anchors"} images={props.anchorImages} />}
                    </ClientInfoContextProvider>
                </PDFProjectContext.Provider>
            </PDF>
        </div>
    )
});